import { useState, useEffect, useMemo } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, TextInput, Modal, TouchableWithoutFeedback, ScrollView } from 'react-native';
import { Calendar, Plus, X, Edit2, Trash2, Check, XCircle, List, ChevronLeft, ChevronRight, Repeat, MapPin, Settings, Users } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';
import MarketsManager from '../../components/MarketsManager';
import WebScrollView from '../../components/WebScrollView';

interface Market {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface WalkthroughAvailability {
  id: string;
  available_date: string;
  available_time_slots: string[];
  max_bookings_per_slot: number;
  is_active: boolean;
  home_manager: string;
  market_id: string | null;
  market_name?: string | null;
  market_walkthrough_type?: 'in_person' | 'virtual' | null;
  booked_counts_by_slot?: Record<string, number> | null;
  created_at: string;
  updated_at: string;
}

interface BookedWalkthroughVisit {
  id: string;
  scheduled_date: string;
  owner_name: string;
}

type DateScope = 'this_month' | 'next_month' | 'all_upcoming';
const WALKTHROUGH_TIMEZONE = 'America/Detroit';
const commonTimeSlots = [
  '8:00 AM',
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM',
  '5:00 PM',
] as const;

const WalkthroughAvailabilityTab = () => {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState<WalkthroughAvailability[]>([]);
  const [bookedVisits, setBookedVisits] = useState<BookedWalkthroughVisit[]>([]);
  const [dateScope, setDateScope] = useState<DateScope>('all_upcoming');
  const [markets, setMarkets] = useState<Market[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showMarketsManager, setShowMarketsManager] = useState(false);
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    available_date: '',
    time_slots_input: '',
    max_bookings_per_slot: '1',
    is_active: true,
    home_manager: 'Unassigned',
    market_id: '',
  });
  const [selectedFormTimeSlots, setSelectedFormTimeSlots] = useState<string[]>([]);
  const [customFormTimeSlot, setCustomFormTimeSlot] = useState('');
  const [recurringFormData, setRecurringFormData] = useState({
    start_date: '',
    end_date: '',
    days_of_week: [] as number[], // 0 = Sunday, 1 = Monday, etc.
    time_slots_input: '',
    max_bookings_per_slot: '1',
    is_active: true,
    home_manager: 'Unassigned',
    market_id: '',
  });
  const [selectedRecurringTimeSlots, setSelectedRecurringTimeSlots] = useState<string[]>([]);
  const [customRecurringTimeSlot, setCustomRecurringTimeSlot] = useState('');
  const [saving, setSaving] = useState(false);
  const [walkthroughHostNames, setWalkthroughHostNames] = useState<string[]>([]);

  const formHomeManagerOptions = useMemo(() => {
    const names = walkthroughHostNames;
    const base = ['Unassigned', ...names];
    const h = formData.home_manager;
    if (h && h !== 'Unassigned' && !base.includes(h)) {
      return ['Unassigned', h, ...names];
    }
    return base;
  }, [walkthroughHostNames, formData.home_manager]);

  const recurringHomeManagerOptions = useMemo(() => {
    const names = walkthroughHostNames;
    const base = ['Unassigned', ...names];
    const h = recurringFormData.home_manager;
    if (h && h !== 'Unassigned' && !base.includes(h)) {
      return ['Unassigned', h, ...names];
    }
    return base;
  }, [walkthroughHostNames, recurringFormData.home_manager]);

  const fetchWalkthroughHosts = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('is_system_manager', true)
        .eq('is_walkthrough_host', true)
        .order('full_name', { ascending: true });
      if (fetchError) throw fetchError;
      setWalkthroughHostNames(
        (data || [])
          .map((p) => p.full_name)
          .filter((n): n is string => Boolean(n && String(n).trim()))
      );
    } catch (err) {
      console.error('Error fetching walkthrough hosts:', err);
    }
  };

  useEffect(() => {
    fetchMarkets();
    fetchAvailability();
    fetchWalkthroughHosts();
  }, []);

  const fetchMarkets = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('markets')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setMarkets(data || []);
    } catch (err: any) {
      console.error('Error fetching markets:', err);
    }
  };

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const todayDate = getTodayDateString();
      let query = supabase
        .from('walkthrough_availability_with_bookings')
        .select('*')
        .gte('available_date', todayDate)
        .order('available_date', { ascending: true });

      if (selectedMarketId) {
        query = query.eq('market_id', selectedMarketId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      
      // Parse booked_counts_by_slot if it's a JSON string
      const processedData = (data || []).map(item => ({
        ...item,
        booked_counts_by_slot: typeof item.booked_counts_by_slot === 'string' 
          ? JSON.parse(item.booked_counts_by_slot) 
          : item.booked_counts_by_slot
      }));

      if (processedData.length === 0) {
        setAvailability([]);
        setBookedVisits([]);
        return;
      }

      const minDate = processedData[0].available_date;
      const maxDate = processedData[processedData.length - 1].available_date;
      const visitRangeStartDate = shiftDateString(minDate, -1);
      const visitRangeEndDate = shiftDateString(maxDate, 1);
      let visitsQuery = supabase
        .from('visits')
        .select(`
          id,
          scheduled_date,
          homes:home_id (
            market_id,
            profiles:owner_id (
              full_name
            )
          )
        `)
        .eq('visit_type', 'walkthrough')
        .in('status', ['scheduled', 'in_progress'])
        .not('scheduled_date', 'is', null)
        .gte('scheduled_date', `${visitRangeStartDate}T00:00:00.000Z`)
        .lte('scheduled_date', `${visitRangeEndDate}T23:59:59.999Z`);

      if (selectedMarketId) {
        visitsQuery = visitsQuery.eq('homes.market_id', selectedMarketId);
      }

      const { data: visitData, error: visitError } = await visitsQuery;
      if (visitError) throw visitError;

      const processedVisits: BookedWalkthroughVisit[] = (visitData || []).map((visit: any) => {
        const home = Array.isArray(visit.homes) ? visit.homes[0] : visit.homes;
        const owner = Array.isArray(home?.profiles) ? home.profiles[0] : home?.profiles;
        return {
          id: visit.id,
          scheduled_date: visit.scheduled_date,
          owner_name: owner?.full_name || 'Unknown homeowner',
        };
      });
      
      setAvailability(processedData);
      setBookedVisits(processedVisits);
    } catch (err: any) {
      console.error('Error fetching availability:', err);
      setError(err.message || 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item?: WalkthroughAvailability) => {
    if (item) {
      setEditingId(item.id);
      setSelectedFormTimeSlots(item.available_time_slots);
      setFormData({
        available_date: item.available_date,
        time_slots_input: item.available_time_slots.join(', '),
        max_bookings_per_slot: item.max_bookings_per_slot.toString(),
        is_active: item.is_active,
        home_manager: item.home_manager || 'Unassigned',
        market_id: item.market_id || '',
      });
    } else {
      setEditingId(null);
      setSelectedFormTimeSlots([]);
      setFormData({
        available_date: '',
        time_slots_input: '',
        max_bookings_per_slot: '1',
        is_active: true,
        home_manager: 'Unassigned',
        market_id: selectedMarketId || '',
      });
    }
    setCustomFormTimeSlot('');
    setShowModal(true);
  };

  const handleOpenRecurringModal = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 3, 0);
    setRecurringFormData({
      start_date: today.toISOString().split('T')[0],
      end_date: nextMonth.toISOString().split('T')[0],
      days_of_week: [],
      time_slots_input: '',
      max_bookings_per_slot: '1',
      is_active: true,
      home_manager: 'Unassigned',
      market_id: selectedMarketId || '',
    });
    setSelectedRecurringTimeSlots([]);
    setCustomRecurringTimeSlot('');
    setShowRecurringModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setSelectedFormTimeSlots([]);
    setCustomFormTimeSlot('');
    setFormData({
      available_date: '',
      time_slots_input: '',
      max_bookings_per_slot: '1',
      is_active: true,
      home_manager: 'Unassigned',
      market_id: '',
    });
  };

  const handleCloseRecurringModal = () => {
    setShowRecurringModal(false);
    setSelectedRecurringTimeSlots([]);
    setCustomRecurringTimeSlot('');
    setRecurringFormData({
      start_date: '',
      end_date: '',
      days_of_week: [],
      time_slots_input: '',
      max_bookings_per_slot: '1',
      is_active: true,
      home_manager: 'Unassigned',
      market_id: '',
    });
  };

  const toggleDayOfWeek = (day: number) => {
    setRecurringFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort()
    }));
  };

  const sortTimeSlots = (slots: string[]) => {
    const toMinutes = (slot: string) => {
      const match = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return Number.MAX_SAFE_INTEGER;
      let hour = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const period = match[3].toUpperCase();
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      return hour * 60 + minutes;
    };

    return [...slots].sort((a, b) => {
      const byTime = toMinutes(a) - toMinutes(b);
      if (byTime !== 0) return byTime;
      return a.localeCompare(b);
    });
  };

  const toggleFormTimeSlot = (slot: string) => {
    const nextSlots = selectedFormTimeSlots.includes(slot)
      ? selectedFormTimeSlots.filter((s) => s !== slot)
      : [...selectedFormTimeSlots, slot];
    const sorted = sortTimeSlots(nextSlots);
    setSelectedFormTimeSlots(sorted);
    setFormData((prev) => ({ ...prev, time_slots_input: sorted.join(', ') }));
  };

  const toggleRecurringTimeSlot = (slot: string) => {
    const nextSlots = selectedRecurringTimeSlots.includes(slot)
      ? selectedRecurringTimeSlots.filter((s) => s !== slot)
      : [...selectedRecurringTimeSlots, slot];
    const sorted = sortTimeSlots(nextSlots);
    setSelectedRecurringTimeSlots(sorted);
    setRecurringFormData((prev) => ({ ...prev, time_slots_input: sorted.join(', ') }));
  };

  const addCustomFormTimeSlot = () => {
    const custom = customFormTimeSlot.trim();
    if (!custom) return;
    if (selectedFormTimeSlots.includes(custom)) {
      setCustomFormTimeSlot('');
      return;
    }
    const sorted = sortTimeSlots([...selectedFormTimeSlots, custom]);
    setSelectedFormTimeSlots(sorted);
    setFormData((prev) => ({ ...prev, time_slots_input: sorted.join(', ') }));
    setCustomFormTimeSlot('');
  };

  const addCustomRecurringTimeSlot = () => {
    const custom = customRecurringTimeSlot.trim();
    if (!custom) return;
    if (selectedRecurringTimeSlots.includes(custom)) {
      setCustomRecurringTimeSlot('');
      return;
    }
    const sorted = sortTimeSlots([...selectedRecurringTimeSlots, custom]);
    setSelectedRecurringTimeSlots(sorted);
    setRecurringFormData((prev) => ({ ...prev, time_slots_input: sorted.join(', ') }));
    setCustomRecurringTimeSlot('');
  };

  const handleSave = async () => {
    if (!formData.available_date) {
      setError('Please select a date');
      return;
    }

    if (!formData.time_slots_input.trim()) {
      setError('Please enter at least one time slot');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const timeSlots = formData.time_slots_input
        .split(',')
        .map(slot => slot.trim())
        .filter(slot => slot.length > 0);

      if (timeSlots.length === 0) {
        throw new Error('Please enter at least one valid time slot');
      }

      const availabilityData = {
        available_date: formData.available_date,
        available_time_slots: timeSlots,
        max_bookings_per_slot: parseInt(formData.max_bookings_per_slot) || 1,
        is_active: formData.is_active,
        home_manager: formData.home_manager,
        market_id: formData.market_id || null,
        created_by: editingId ? undefined : user.id,
      };

      if (editingId) {
        const { error: updateError } = await supabase
          .from('walkthrough_availability')
          .update(availabilityData)
          .eq('id', editingId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('walkthrough_availability')
          .insert([availabilityData]);

        if (insertError) throw insertError;
      }

      await fetchAvailability();
      await fetchMarkets();
      handleCloseModal();
    } catch (err: any) {
      console.error('Error saving availability:', err);
      setError(err.message || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRecurring = async () => {
    if (!recurringFormData.start_date || !recurringFormData.end_date) {
      setError('Please select start and end dates');
      return;
    }

    if (recurringFormData.days_of_week.length === 0) {
      setError('Please select at least one day of the week');
      return;
    }

    if (!recurringFormData.time_slots_input.trim()) {
      setError('Please enter at least one time slot');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const timeSlots = recurringFormData.time_slots_input
        .split(',')
        .map(slot => slot.trim())
        .filter(slot => slot.length > 0);

      if (timeSlots.length === 0) {
        throw new Error('Please enter at least one valid time slot');
      }

      const startDate = new Date(recurringFormData.start_date);
      const endDate = new Date(recurringFormData.end_date);
      const datesToCreate: string[] = [];

      // Generate all dates for selected days of week
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        if (recurringFormData.days_of_week.includes(dayOfWeek)) {
          datesToCreate.push(currentDate.toISOString().split('T')[0]);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Check for existing dates and only create new ones
      const existingDates = new Set(availability.map(a => a.available_date));
      const newDates = datesToCreate.filter(date => !existingDates.has(date));

      if (newDates.length === 0) {
        throw new Error('All dates in this range already have availability set');
      }

      const availabilityData = newDates.map(date => ({
        available_date: date,
        available_time_slots: timeSlots,
        max_bookings_per_slot: parseInt(recurringFormData.max_bookings_per_slot) || 1,
        is_active: recurringFormData.is_active,
        home_manager: recurringFormData.home_manager,
        market_id: recurringFormData.market_id || null,
        created_by: user.id,
      }));

      const { error: insertError } = await supabase
        .from('walkthrough_availability')
        .insert(availabilityData);

      if (insertError) throw insertError;

      await fetchAvailability();
      await fetchMarkets();
      handleCloseRecurringModal();
    } catch (err: any) {
      console.error('Error saving recurring availability:', err);
      setError(err.message || 'Failed to save recurring availability');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this availability?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('walkthrough_availability')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchAvailability();
    } catch (err: any) {
      console.error('Error deleting availability:', err);
      setError(err.message || 'Failed to delete availability');
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [selectedMarketId]);

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error: updateError } = await supabase
        .from('walkthrough_availability')
        .update({ is_active: !currentActive })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchAvailability();
    } catch (err: any) {
      console.error('Error toggling active status:', err);
      setError(err.message || 'Failed to update availability');
    }
  };

  const formatDate = (dateString: string) => {
    // Parse YYYY-MM-DD as local date, not UTC, to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getAvailabilityForDate = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return filteredAvailability.find(a => a.available_date === dateStr);
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const filteredAvailability = useMemo(() => {
    if (dateScope === 'all_upcoming') {
      return availability;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIndex = now.getMonth();

    if (dateScope === 'this_month') {
      const monthStart = `${currentYear}-${String(currentMonthIndex + 1).padStart(2, '0')}-01`;
      const monthEnd = `${currentYear}-${String(currentMonthIndex + 1).padStart(2, '0')}-${String(new Date(currentYear, currentMonthIndex + 1, 0).getDate()).padStart(2, '0')}`;
      return availability.filter(item => item.available_date >= monthStart && item.available_date <= monthEnd);
    }

    const nextMonthDate = new Date(currentYear, currentMonthIndex + 1, 1);
    const nextMonthYear = nextMonthDate.getFullYear();
    const nextMonthIndex = nextMonthDate.getMonth();
    const nextMonthStart = `${nextMonthYear}-${String(nextMonthIndex + 1).padStart(2, '0')}-01`;
    const nextMonthEnd = `${nextMonthYear}-${String(nextMonthIndex + 1).padStart(2, '0')}-${String(new Date(nextMonthYear, nextMonthIndex + 1, 0).getDate()).padStart(2, '0')}`;

    return availability.filter(item => item.available_date >= nextMonthStart && item.available_date <= nextMonthEnd);
  }, [availability, dateScope]);

  const parseTimeSlotToHour = (timeSlot: string) => {
    const match = timeSlot.trim().match(/^(\d{1,2})(?::\d{2})?\s*(AM|PM)$/i);
    if (!match) return -1;
    let hour = parseInt(match[1], 10);
    const period = match[2].toUpperCase();

    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }

    return hour;
  };

  const shiftDateString = (dateString: string, days: number) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDateInTimezone = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value || '0000';
    const month = parts.find((part) => part.type === 'month')?.value || '01';
    const day = parts.find((part) => part.type === 'day')?.value || '01';

    return `${year}-${month}-${day}`;
  };

  const getHourInTimezone = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hour = parts.find((part) => part.type === 'hour')?.value || '00';
    return parseInt(hour, 10);
  };

  const bookedVisitsByAvailabilitySlot = useMemo(() => {
    const map: Record<string, BookedWalkthroughVisit[]> = {};

    filteredAvailability.forEach((item) => {
      item.available_time_slots.forEach((slot) => {
        const slotHour = parseTimeSlotToHour(slot);
        const key = `${item.id}::${slot}`;
        map[key] = bookedVisits.filter((visit) => {
          const visitDate = new Date(visit.scheduled_date);
          const visitDateOnly = getDateInTimezone(visitDate, WALKTHROUGH_TIMEZONE);
          return visitDateOnly === item.available_date && getHourInTimezone(visitDate, WALKTHROUGH_TIMEZONE) === slotHour;
        });
      });
    });

    return map;
  }, [filteredAvailability, bookedVisits]);

  const openVisitDetail = (visitId: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = `/admin/visit?id=${visitId}`;
    }
  };

  const getSlotStatus = (bookedCount: number, maxBookingsPerSlot: number) => {
    if (bookedCount >= maxBookingsPerSlot) {
      return 'full';
    }
    if (bookedCount > 0) {
      return 'partial';
    }
    return 'open';
  };

  useEffect(() => {
    const now = new Date();
    if (dateScope === 'this_month' || dateScope === 'all_upcoming') {
      setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      return;
    }
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1));
  }, [dateScope]);

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return (
      <View style={styles.calendarContainer}>
        <View style={styles.calendarHeader}>
          <Pressable onPress={previousMonth} style={styles.calendarNavButton}>
            <ChevronLeft size={20} color="#111827" />
          </Pressable>
          <Text style={styles.calendarMonthText}>
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Text>
          <Pressable onPress={nextMonth} style={styles.calendarNavButton}>
            <ChevronRight size={20} color="#111827" />
          </Pressable>
        </View>

        <View style={styles.calendarGrid}>
          {/* Day names header */}
          {dayNames.map((dayName) => (
            <View key={dayName} style={styles.calendarDayHeader}>
              <Text style={styles.calendarDayHeaderText}>{dayName}</Text>
            </View>
          ))}

          {/* Calendar days */}
          {days.map((date, index) => {
            if (!date) {
              return <View key={`empty-${index}`} style={styles.calendarDay} />;
            }

            const avail = getAvailabilityForDate(date);
            const isToday = isSameDay(date, new Date());
            const isPast = date < new Date() && !isToday;
            const displayedSlots = avail ? avail.available_time_slots.slice(0, 3) : [];
            const hiddenSlotCount = avail ? Math.max(avail.available_time_slots.length - displayedSlots.length, 0) : 0;

            return (
              <Pressable
                key={date.toISOString()}
                onPress={() => {
                  if (avail) {
                    handleOpenModal(avail);
                  } else {
                    // Format date as YYYY-MM-DD without timezone conversion
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setFormData({
                      available_date: `${year}-${month}-${day}`,
                      time_slots_input: '',
                      max_bookings_per_slot: '1',
                      is_active: true,
                      home_manager: 'Unassigned',
                      market_id: selectedMarketId || '',
                    });
                    setEditingId(null);
                    setShowModal(true);
                  }
                }}
                style={[
                  styles.calendarDay,
                  avail && styles.calendarDayWithAvailability,
                  isToday && styles.calendarDayToday,
                  isPast && styles.calendarDayPast,
                ]}
              >
                <View style={styles.calendarDayTopRow}>
                  <Text style={[
                    styles.calendarDayNumber,
                    avail && styles.calendarDayNumberWithAvailability,
                    isPast && styles.calendarDayNumberPast,
                  ]}>
                    {date.getDate()}
                  </Text>
                  {avail && (
                    <View style={[
                      styles.calendarDayIndicator,
                      !avail.is_active && styles.calendarDayIndicatorInactive
                    ]} />
                  )}
                </View>

                {avail && (
                  <View style={styles.calendarSlotsContainer}>
                    {displayedSlots.map((slot) => {
                      const bookedCount = avail.booked_counts_by_slot?.[slot] || 0;
                      const status = getSlotStatus(bookedCount, avail.max_bookings_per_slot);
                      return (
                        <View key={`${avail.id}-${slot}`} style={styles.calendarSlotRow}>
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.calendarSlotText,
                              status === 'full' && styles.calendarSlotTextFull,
                              status === 'partial' && styles.calendarSlotTextPartial,
                            ]}
                          >
                            {slot} {bookedCount}/{avail.max_bookings_per_slot}
                          </Text>
                        </View>
                      );
                    })}
                    {hiddenSlotCount > 0 && (
                      <Text style={styles.calendarMoreSlotsText}>+{hiddenSlotCount} more</Text>
                    )}
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  if (showMarketsManager) {
    return (
      <View style={styles.outer}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Pressable onPress={() => setShowMarketsManager(false)} style={styles.backButton}>
              <ChevronLeft size={20} color="#111827" />
            </Pressable>
            <MapPin size={24} color="#111827" />
            <Text style={styles.title}>Markets</Text>
          </View>
        </View>
        <MarketsManager 
          onMarketSelect={(marketId) => {
            setSelectedMarketId(marketId);
            setShowMarketsManager(false);
          }}
          selectedMarketId={selectedMarketId}
        />
      </View>
    );
  }

  return (
    <View style={styles.outer}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Calendar size={24} color="#111827" />
          <Text style={styles.title}>Walkthrough Availability</Text>
        </View>
        <View style={styles.headerActions}>
          {/* Market Filter */}
          <View style={styles.marketFilter}>
            {Platform.OS === 'web' ? (
              <select
                value={selectedMarketId || ''}
                onChange={(e) => setSelectedMarketId(e.target.value || null)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 14,
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Markets</option>
                {markets.map((market) => (
                  <option key={market.id} value={market.id}>
                    {market.name}
                  </option>
                ))}
              </select>
            ) : (
              <Text style={styles.marketFilterText}>
                {selectedMarketId ? markets.find(m => m.id === selectedMarketId)?.name || 'Market' : 'All Markets'}
              </Text>
            )}
          </View>
          {/* Markets Manager Button */}
          <Pressable
            onPress={() => setShowMarketsManager(true)}
            style={styles.marketsButton}
          >
            <Settings size={16} color="#ffffff" />
            <Text style={styles.marketsButtonText}>Markets</Text>
          </Pressable>
          {/* View Mode Toggle */}
          <View style={styles.viewToggle}>
            <Pressable
              onPress={() => setViewMode('list')}
              style={[
                styles.viewToggleButton,
                viewMode === 'list' && styles.viewToggleButtonActive
              ]}
            >
              <List size={16} color={viewMode === 'list' ? "#ffffff" : "#6b7280"} />
              <Text style={[
                styles.viewToggleButtonText,
                viewMode === 'list' && styles.viewToggleButtonTextActive
              ]}>
                List
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setViewMode('calendar')}
              style={[
                styles.viewToggleButton,
                viewMode === 'calendar' && styles.viewToggleButtonActive
              ]}
            >
              <Calendar size={16} color={viewMode === 'calendar' ? "#ffffff" : "#6b7280"} />
              <Text style={[
                styles.viewToggleButtonText,
                viewMode === 'calendar' && styles.viewToggleButtonTextActive
              ]}>
                Calendar
              </Text>
            </Pressable>
          </View>
          {/* Date Scope Toggle */}
          <View style={styles.viewToggle}>
            <Pressable
              onPress={() => setDateScope('this_month')}
              style={[
                styles.viewToggleButton,
                dateScope === 'this_month' && styles.viewToggleButtonActive
              ]}
            >
              <Text style={[
                styles.viewToggleButtonText,
                dateScope === 'this_month' && styles.viewToggleButtonTextActive
              ]}>
                This Month
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setDateScope('next_month')}
              style={[
                styles.viewToggleButton,
                dateScope === 'next_month' && styles.viewToggleButtonActive
              ]}
            >
              <Text style={[
                styles.viewToggleButtonText,
                dateScope === 'next_month' && styles.viewToggleButtonTextActive
              ]}>
                Next Month
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setDateScope('all_upcoming')}
              style={[
                styles.viewToggleButton,
                dateScope === 'all_upcoming' && styles.viewToggleButtonActive
              ]}
            >
              <Text style={[
                styles.viewToggleButtonText,
                dateScope === 'all_upcoming' && styles.viewToggleButtonTextActive
              ]}>
                All Upcoming
              </Text>
            </Pressable>
          </View>

          {/* Add Buttons */}
          <Pressable
            onPress={handleOpenRecurringModal}
            style={[styles.addButton, styles.recurringButton]}
          >
            <Repeat size={16} color="#ffffff" />
            <Text style={styles.addButtonText}>Recurring</Text>
          </Pressable>
          <Pressable
            onPress={() => handleOpenModal()}
            style={styles.addButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.addButtonText}>Add Date</Text>
          </Pressable>
        </View>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <WebScrollView style={styles.mainScroll} contentContainerStyle={styles.mainScrollContent}>
      {viewMode === 'calendar' ? (
        renderCalendar()
      ) : filteredAvailability.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Calendar size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Upcoming Availability</Text>
          <Text style={styles.emptyText}>
            There are no dates for the selected upcoming date range.
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {filteredAvailability.map((item) => (
            <View key={item.id} style={styles.item}>
              <View style={styles.itemContent}>
                <View style={styles.itemHeader}>
                  <View style={styles.itemHeaderLeft}>
                    <Calendar size={20} color={item.is_active ? "#2563eb" : "#9ca3af"} />
                    <View>
                      <Text style={[
                        styles.itemDate,
                        !item.is_active && styles.itemDateInactive
                      ]}>
                        {formatDate(item.available_date)}
                      </Text>
                      <Text style={styles.itemSlots}>
                        {item.available_time_slots.length} time slot{item.available_time_slots.length !== 1 ? 's' : ''}
                      </Text>
                      <Text style={styles.itemHomeManager}>
                        Assigned to: {item.home_manager || 'Unassigned'}
                      </Text>
                      {item.market_name && (
                        <View style={styles.marketInfo}>
                          <Text style={styles.itemMarket}>
                            Market: {item.market_name}
                          </Text>
                          {item.market_walkthrough_type && (
                            <Text style={styles.itemWalkthroughType}>
                              {item.market_walkthrough_type === 'virtual' ? '📹 Virtual' : '🏠 In-Person'}
                            </Text>
                          )}
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={styles.itemBadges}>
                    {item.is_active ? (
                      <View style={styles.activeBadge}>
                        <Text style={styles.activeBadgeText}>Active</Text>
                      </View>
                    ) : (
                      <View style={styles.inactiveBadge}>
                        <Text style={styles.inactiveBadgeText}>Inactive</Text>
                      </View>
                    )}
                  </View>
                </View>
                <View style={styles.timeSlotsContainer}>
                  {item.available_time_slots.map((slot, index) => {
                    const bookedCount = item.booked_counts_by_slot?.[slot] || 0;
                    const isFull = bookedCount >= item.max_bookings_per_slot;
                    const isPartiallyBooked = bookedCount > 0 && bookedCount < item.max_bookings_per_slot;
                    return (
                      <View 
                        key={index} 
                        style={[
                          styles.timeSlotBadge,
                          isFull && styles.timeSlotBadgeFull,
                          isPartiallyBooked && styles.timeSlotBadgePartial
                        ]}
                      >
                        <Text style={styles.timeSlotText}>{slot}</Text>
                        {bookedCount > 0 && (
                          <View style={styles.bookedCountBadge}>
                            <Users size={12} color="#ffffff" />
                            <Text style={styles.bookedCountText}>
                              {bookedCount}/{item.max_bookings_per_slot}
                            </Text>
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
                {item.available_time_slots.map((slot, index) => {
                  const slotBookedVisits = bookedVisitsByAvailabilitySlot[`${item.id}::${slot}`] || [];
                  if (slotBookedVisits.length === 0) {
                    return null;
                  }

                  return (
                    <View key={`booked-${item.id}-${index}`} style={styles.bookedVisitsRow}>
                      <Text style={styles.bookedVisitsLabel}>{slot} booked by:</Text>
                      <View style={styles.bookedVisitorsList}>
                        {slotBookedVisits.map((visit) => (
                          <Pressable
                            key={visit.id}
                            onPress={() => openVisitDetail(visit.id)}
                            style={styles.bookedVisitorChip}
                          >
                            <Text style={styles.bookedVisitorChipText}>{visit.owner_name}</Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  );
                })}
                <Text style={styles.maxBookingsText}>
                  Max bookings per slot: {item.max_bookings_per_slot}
                </Text>
              </View>
              <View style={styles.itemActions}>
                <Pressable
                  onPress={() => handleToggleActive(item.id, item.is_active)}
                  style={styles.actionButton}
                >
                  {item.is_active ? (
                    <XCircle size={18} color="#dc2626" />
                  ) : (
                    <Check size={18} color="#16a34a" />
                  )}
                  <Text style={[
                    styles.actionButtonText,
                    item.is_active ? styles.deactivateText : styles.activateText
                  ]}>
                    {item.is_active ? 'Deactivate' : 'Activate'}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => handleOpenModal(item)}
                  style={styles.actionButton}
                >
                  <Edit2 size={18} color="#2563eb" />
                  <Text style={[styles.actionButtonText, styles.editText]}>Edit</Text>
                </Pressable>
                <Pressable
                  onPress={() => handleDelete(item.id)}
                  style={styles.actionButton}
                >
                  <Trash2 size={18} color="#dc2626" />
                  <Text style={[styles.actionButtonText, styles.deleteText]}>Delete</Text>
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      )}
      </WebScrollView>

      {/* Add/Edit Single Date Modal */}
      <Modal
        visible={showModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <ScrollView style={styles.modalContent} contentContainerStyle={styles.modalContentContainer}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>
                    {editingId ? 'Edit Availability' : 'Add Availability'}
                  </Text>
                  <Pressable onPress={handleCloseModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                <View style={styles.form}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Date</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.available_date}
                        onChange={(e) => setFormData({ ...formData, available_date: e.target.value })}
                        style={{ 
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                        }}
                      />
                    ) : (
                      <TextInput
                        style={styles.input}
                        value={formData.available_date}
                        onChangeText={(text) => setFormData({ ...formData, available_date: text })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Time Slots</Text>
                    <View style={styles.slotPickerContainer}>
                      {commonTimeSlots.map((slot) => (
                        <Pressable
                          key={slot}
                          onPress={() => toggleFormTimeSlot(slot)}
                          style={[
                            styles.slotOptionButton,
                            selectedFormTimeSlots.includes(slot) && styles.slotOptionButtonSelected
                          ]}
                        >
                          <Text style={[
                            styles.slotOptionButtonText,
                            selectedFormTimeSlots.includes(slot) && styles.slotOptionButtonTextSelected
                          ]}>
                            {slot}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                    <View style={styles.customSlotRow}>
                      <TextInput
                        style={[styles.input, styles.customSlotInput]}
                        value={customFormTimeSlot}
                        onChangeText={setCustomFormTimeSlot}
                        placeholder="Add custom slot (e.g., 6:30 PM)"
                        placeholderTextColor="#9ca3af"
                      />
                      <Pressable onPress={addCustomFormTimeSlot} style={styles.customSlotAddButton}>
                        <Plus size={16} color="#ffffff" />
                        <Text style={styles.customSlotAddButtonText}>Add</Text>
                      </Pressable>
                    </View>
                    {selectedFormTimeSlots.length > 0 && (
                      <View style={styles.selectedSlotsPreview}>
                        <Text style={styles.selectedSlotsPreviewLabel}>Selected:</Text>
                        <Text style={styles.selectedSlotsPreviewText}>
                          {selectedFormTimeSlots.join(', ')}
                        </Text>
                      </View>
                    )}
                    <Text style={styles.helpText}>
                      Select one or more slots. This will be saved as a comma-separated list.
                    </Text>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Max Bookings Per Slot</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.max_bookings_per_slot}
                      onChangeText={(text) => setFormData({ ...formData, max_bookings_per_slot: text })}
                      placeholder="1"
                      placeholderTextColor="#9ca3af"
                      keyboardType="numeric"
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Market</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={formData.market_id}
                        onChange={(e) => setFormData({ ...formData, market_id: e.target.value })}
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <option value="">No Market</option>
                        {markets.map((market) => (
                          <option key={market.id} value={market.id}>
                            {market.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        <TextInput
                          style={styles.input}
                          value={markets.find(m => m.id === formData.market_id)?.name || 'No Market'}
                          editable={false}
                          placeholder="Select market"
                          placeholderTextColor="#9ca3af"
                        />
                      </View>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Home Manager</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={formData.home_manager}
                        onChange={(e) => setFormData({ ...formData, home_manager: e.target.value })}
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {formHomeManagerOptions.map((manager) => (
                          <option key={manager} value={manager}>
                            {manager}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        {/* For React Native, you'd use Picker component here */}
                        <TextInput
                          style={styles.input}
                          value={formData.home_manager}
                          editable={false}
                          placeholder="Select home manager"
                          placeholderTextColor="#9ca3af"
                        />
                      </View>
                    )}
                    {Platform.OS === 'web' && walkthroughHostNames.length === 0 && (
                      <Text style={styles.helpText}>
                        No team members are enabled for walkthroughs yet. A super admin can enable them under Team.
                      </Text>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Pressable
                      onPress={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      style={styles.checkboxContainer}
                    >
                      <View style={[
                        styles.checkbox,
                        formData.is_active && styles.checkboxChecked
                      ]}>
                        {formData.is_active && <Check size={16} color="#ffffff" />}
                      </View>
                      <Text style={styles.checkboxLabel}>Active</Text>
                    </Pressable>
                  </View>

                  {error && (
                    <View style={styles.errorContainer}>
                      <Text style={styles.errorText}>{error}</Text>
                    </View>
                  )}

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={handleCloseModal}
                      style={styles.cancelButton}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleSave}
                      disabled={saving}
                      style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                    >
                      {saving ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <Text style={styles.saveButtonText}>Save</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </ScrollView>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Recurring Schedule Modal */}
      <Modal
        visible={showRecurringModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseRecurringModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseRecurringModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <ScrollView style={styles.modalContent} contentContainerStyle={styles.modalContentContainer}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Create Recurring Schedule</Text>
                  <Pressable onPress={handleCloseRecurringModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                <View style={styles.form}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Start Date</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={recurringFormData.start_date}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, start_date: e.target.value })}
                        style={{ 
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                        }}
                      />
                    ) : (
                      <TextInput
                        style={styles.input}
                        value={recurringFormData.start_date}
                        onChangeText={(text) => setRecurringFormData({ ...recurringFormData, start_date: text })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>End Date</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={recurringFormData.end_date}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, end_date: e.target.value })}
                        style={{ 
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                        }}
                      />
                    ) : (
                      <TextInput
                        style={styles.input}
                        value={recurringFormData.end_date}
                        onChangeText={(text) => setRecurringFormData({ ...recurringFormData, end_date: text })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Days of Week</Text>
                    <View style={styles.daysOfWeekContainer}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                        <Pressable
                          key={day}
                          onPress={() => toggleDayOfWeek(index)}
                          style={[
                            styles.dayOfWeekButton,
                            recurringFormData.days_of_week.includes(index) && styles.dayOfWeekButtonSelected
                          ]}
                        >
                          <Text style={[
                            styles.dayOfWeekButtonText,
                            recurringFormData.days_of_week.includes(index) && styles.dayOfWeekButtonTextSelected
                          ]}>
                            {day}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Time Slots</Text>
                    <View style={styles.slotPickerContainer}>
                      {commonTimeSlots.map((slot) => (
                        <Pressable
                          key={`recurring-${slot}`}
                          onPress={() => toggleRecurringTimeSlot(slot)}
                          style={[
                            styles.slotOptionButton,
                            selectedRecurringTimeSlots.includes(slot) && styles.slotOptionButtonSelected
                          ]}
                        >
                          <Text style={[
                            styles.slotOptionButtonText,
                            selectedRecurringTimeSlots.includes(slot) && styles.slotOptionButtonTextSelected
                          ]}>
                            {slot}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                    <View style={styles.customSlotRow}>
                      <TextInput
                        style={[styles.input, styles.customSlotInput]}
                        value={customRecurringTimeSlot}
                        onChangeText={setCustomRecurringTimeSlot}
                        placeholder="Add custom slot (e.g., 6:30 PM)"
                        placeholderTextColor="#9ca3af"
                      />
                      <Pressable onPress={addCustomRecurringTimeSlot} style={styles.customSlotAddButton}>
                        <Plus size={16} color="#ffffff" />
                        <Text style={styles.customSlotAddButtonText}>Add</Text>
                      </Pressable>
                    </View>
                    {selectedRecurringTimeSlots.length > 0 && (
                      <View style={styles.selectedSlotsPreview}>
                        <Text style={styles.selectedSlotsPreviewLabel}>Selected:</Text>
                        <Text style={styles.selectedSlotsPreviewText}>
                          {selectedRecurringTimeSlots.join(', ')}
                        </Text>
                      </View>
                    )}
                    <Text style={styles.helpText}>
                      Select one or more slots. This will be saved as a comma-separated list.
                    </Text>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Max Bookings Per Slot</Text>
                    <TextInput
                      style={styles.input}
                      value={recurringFormData.max_bookings_per_slot}
                      onChangeText={(text) => setRecurringFormData({ ...recurringFormData, max_bookings_per_slot: text })}
                      placeholder="1"
                      placeholderTextColor="#9ca3af"
                      keyboardType="numeric"
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Market</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={recurringFormData.market_id}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, market_id: e.target.value })}
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <option value="">No Market</option>
                        {markets.map((market) => (
                          <option key={market.id} value={market.id}>
                            {market.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        <TextInput
                          style={styles.input}
                          value={markets.find(m => m.id === recurringFormData.market_id)?.name || 'No Market'}
                          editable={false}
                          placeholder="Select market"
                          placeholderTextColor="#9ca3af"
                        />
                      </View>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Home Manager</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={recurringFormData.home_manager}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, home_manager: e.target.value })}
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {recurringHomeManagerOptions.map((manager) => (
                          <option key={manager} value={manager}>
                            {manager}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        {/* For React Native, you'd use Picker component here */}
                        <TextInput
                          style={styles.input}
                          value={recurringFormData.home_manager}
                          editable={false}
                          placeholder="Select home manager"
                          placeholderTextColor="#9ca3af"
                        />
                      </View>
                    )}
                    {Platform.OS === 'web' && walkthroughHostNames.length === 0 && (
                      <Text style={styles.helpText}>
                        No team members are enabled for walkthroughs yet. A super admin can enable them under Team.
                      </Text>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Pressable
                      onPress={() => setRecurringFormData({ ...recurringFormData, is_active: !recurringFormData.is_active })}
                      style={styles.checkboxContainer}
                    >
                      <View style={[
                        styles.checkbox,
                        recurringFormData.is_active && styles.checkboxChecked
                      ]}>
                        {recurringFormData.is_active && <Check size={16} color="#ffffff" />}
                      </View>
                      <Text style={styles.checkboxLabel}>Active</Text>
                    </Pressable>
                  </View>

                  {error && (
                    <View style={styles.errorContainer}>
                      <Text style={styles.errorText}>{error}</Text>
                    </View>
                  )}

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={handleCloseRecurringModal}
                      style={styles.cancelButton}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleSaveRecurring}
                      disabled={saving}
                      style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                    >
                      {saving ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <Text style={styles.saveButtonText}>Create Schedule</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </ScrollView>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  outer: {
    flex: 1,
    minHeight: 0,
    backgroundColor: '#ffffff',
    padding: 16,
    gap: 16,
  },
  mainScroll: {
    flex: 1,
    minHeight: 0,
  },
  mainScrollContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 2,
    gap: 2,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  viewToggleButtonActive: {
    backgroundColor: '#000000',
  },
  viewToggleButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  viewToggleButtonTextActive: {
    color: '#ffffff',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  recurringButton: {
    backgroundColor: '#2563eb',
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  emptyContainer: {
    padding: 60,
    alignItems: 'center',
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
  list: {
    gap: 12,
  },
  item: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 12,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  itemContent: {
    gap: 12,
  },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  itemHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  itemDate: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  itemDateInactive: {
    color: '#9ca3af',
  },
  itemSlots: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  itemHomeManager: {
    fontSize: 13,
    color: '#4b5563',
    marginTop: 4,
    fontWeight: '500',
  },
  itemMarket: {
    fontSize: 13,
    color: '#4b5563',
    marginTop: 4,
    fontWeight: '500',
  },
  marketInfo: {
    marginTop: 4,
    gap: 4,
  },
  itemWalkthroughType: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  marketFilter: {
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  marketFilterText: {
    fontSize: 14,
    color: '#374151',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  marketsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#6b7280',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  marketsButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  backButton: {
    padding: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  itemBadges: {
    gap: 8,
  },
  activeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#dcfce7',
    borderRadius: 12,
  },
  activeBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#16a34a',
  },
  inactiveBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
  },
  inactiveBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  timeSlotsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  timeSlotBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  timeSlotBadgeFull: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
  },
  timeSlotBadgePartial: {
    backgroundColor: '#fef3c7',
    borderColor: '#fbbf24',
  },
  timeSlotText: {
    fontSize: 14,
    color: '#111827',
  },
  bookedCountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#2563eb',
    borderRadius: 4,
  },
  bookedCountText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  maxBookingsText: {
    fontSize: 14,
    color: '#6b7280',
  },
  bookedVisitsRow: {
    marginTop: 8,
    gap: 6,
  },
  bookedVisitsLabel: {
    fontSize: 13,
    color: '#4b5563',
    fontWeight: '500',
  },
  bookedVisitorsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  bookedVisitorChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    backgroundColor: '#eff6ff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  bookedVisitorChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1d4ed8',
  },
  itemActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  editText: {
    color: '#2563eb',
  },
  deleteText: {
    color: '#dc2626',
  },
  activateText: {
    color: '#16a34a',
  },
  deactivateText: {
    color: '#dc2626',
  },
  calendarContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  calendarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  calendarNavButton: {
    padding: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  calendarMonthText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarDayHeader: {
    width: '14.28%',
    paddingVertical: 8,
    alignItems: 'center',
  },
  calendarDayHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  calendarDay: {
    width: '14.28%',
    minHeight: 110,
    padding: 6,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  calendarDayWithAvailability: {
    backgroundColor: '#eff6ff',
    borderColor: '#bfdbfe',
  },
  calendarDayToday: {
    backgroundColor: '#fef3c7',
    borderColor: '#fbbf24',
  },
  calendarDayPast: {
    opacity: 0.5,
  },
  calendarDayNumber: {
    fontSize: 14,
    color: '#111827',
  },
  calendarDayNumberWithAvailability: {
    fontWeight: '600',
    color: '#2563eb',
  },
  calendarDayNumberPast: {
    color: '#9ca3af',
  },
  calendarDayTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  calendarSlotsContainer: {
    gap: 2,
  },
  calendarSlotRow: {
    backgroundColor: '#ffffff',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  calendarSlotText: {
    fontSize: 10,
    color: '#065f46',
    fontWeight: '600',
  },
  calendarSlotTextPartial: {
    color: '#b45309',
  },
  calendarSlotTextFull: {
    color: '#b91c1c',
  },
  calendarMoreSlotsText: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 1,
  },
  calendarDayIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#2563eb',
    marginTop: 2,
  },
  calendarDayIndicatorInactive: {
    backgroundColor: '#9ca3af',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      marginHorizontal: 'auto',
      borderRadius: 12,
      marginBottom: 20,
    }),
  },
  modalContentContainer: {
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  form: {
    gap: 20,
  },
  formGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  textArea: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  slotPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  slotOptionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  slotOptionButtonSelected: {
    backgroundColor: '#111827',
    borderColor: '#111827',
  },
  slotOptionButtonText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  slotOptionButtonTextSelected: {
    color: '#ffffff',
  },
  customSlotRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  customSlotInput: {
    flex: 1,
  },
  customSlotAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#111827',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  customSlotAddButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  selectedSlotsPreview: {
    marginTop: 8,
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 4,
  },
  selectedSlotsPreviewLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  selectedSlotsPreviewText: {
    fontSize: 13,
    color: '#111827',
    fontWeight: '500',
  },
  helpText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  daysOfWeekContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  dayOfWeekButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    minWidth: 50,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  dayOfWeekButtonSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  dayOfWeekButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  dayOfWeekButtonTextSelected: {
    color: '#ffffff',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#d1d5db',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#374151',
  },
  pickerContainer: {
    marginTop: 8,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default WalkthroughAvailabilityTab;
